/**
 * Recusa por corretora inativa: a Api responde 403 com `detail` começando por
 * "Corretora inativa." em qualquer escrita de cotação, proposta, usuário ou
 * produto da corretora.
 *
 * Vários fluxos (ex.: o "Próximo" dos wizards) tratavam esse erro só inline ou
 * nem tratavam. O BaseService passa a avisar por toast, e o toaster junta todos
 * os toasts com essa mensagem num único id — assim a tela que já tinha o seu
 * próprio toast não mostra o aviso duas vezes.
 */
export const INACTIVE_BROKER_TOAST_ID = 'inactive-broker';
export const INACTIVE_BROKER_TOAST_TITLE = 'Corretora inativa';

export function isInactiveBrokerMessage(message: unknown): message is string {
  return (
    typeof message === 'string' &&
    message.trim().startsWith('Corretora inativa.')
  );
}

/** "Corretora inativa. Não é possível X." → "Não é possível X." */
export function stripInactiveBrokerPrefix(message: string): string {
  const rest = message.trim().slice('Corretora inativa.'.length).trim();
  return rest || message.trim();
}

/** Deve disparar o toast global: só 403 com a mensagem de corretora inativa. */
export function shouldNotifyInactiveBroker(
  status: number,
  message: unknown
): message is string {
  return status === 403 && isInactiveBrokerMessage(message);
}

/**
 * Normaliza as opções de um toast: mensagem de corretora inativa vira sempre o
 * mesmo toast (id fixo, tipo erro, título padrão), independente de quem criou.
 */
export function withInactiveBrokerToastDedupe<
  T extends {
    description?: unknown;
    id?: string;
    type?: string;
    title?: unknown;
  },
>(options: T): T {
  if (!isInactiveBrokerMessage(options?.description)) return options;

  return {
    ...options,
    id: INACTIVE_BROKER_TOAST_ID,
    type: 'error',
    title: INACTIVE_BROKER_TOAST_TITLE,
    // O título já diz "Corretora inativa"; o corpo fica só com o que não pode.
    description: stripInactiveBrokerPrefix(options.description),
  };
}
